/**
 * Komal OS - KRA Cycle Store
 * Powers both "My KRA" (employee self-eval) and "Team KRA" (HOD review) -
 * same underlying resource, the pages just call different subsets of these
 * actions depending on who's logged in.
 */

import { create } from 'zustand';
import * as kraCycleService from '../services/kraCycleService';

const useKraCycleStore = create((set, get) => ({
  cycles: [],
  selectedCycle: null,
  total: 0,
  isLoading: false,
  isSubmitting: false,
  error: null,

  clearError: () => set({ error: null }),

  generateCycles: async (month, departmentId) => {
    set({ isSubmitting: true, error: null });
    try {
      const response = await kraCycleService.generateKraCycles({
        month,
        ...(departmentId ? { department_id: Number(departmentId) } : {}),
      });
      set({ isSubmitting: false });
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to generate cycles';
      set({ error: message, isSubmitting: false });
      return { success: false, error: message };
    }
  },

  fetchCycles: async (params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const response = await kraCycleService.getKraCycles(params);
      set({ cycles: response.data.cycles || [], total: response.data.total || 0, isLoading: false });
      return response.data.cycles;
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to load KRA cycles';
      set({ error: message, isLoading: false });
      return [];
    }
  },

  fetchCycle: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await kraCycleService.getKraCycle(id);
      set({ selectedCycle: response.data.cycle, isLoading: false });
      return response.data.cycle;
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to load KRA record';
      set({ error: message, isLoading: false });
      return null;
    }
  },

  saveSelfResponses: async (cycleId, responses) => {
    set({ isSubmitting: true, error: null });
    try {
      await kraCycleService.saveSelfResponses(cycleId, responses);
      await get().fetchCycle(cycleId);
      set({ isSubmitting: false });
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to save self-score';
      set({ error: message, isSubmitting: false });
      return { success: false, error: message };
    }
  },

  submitSelfResponses: async (cycleId) => {
    set({ isSubmitting: true, error: null });
    try {
      const response = await kraCycleService.submitSelfResponses(cycleId);
      await get().fetchCycle(cycleId);
      set({ isSubmitting: false });
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to submit self-evaluation';
      set({ error: message, isSubmitting: false });
      return { success: false, error: message };
    }
  },

  saveHodResponses: async (cycleId, responses) => {
    set({ isSubmitting: true, error: null });
    try {
      const response = await kraCycleService.saveHodResponses(cycleId, responses);
      await get().fetchCycle(cycleId);
      set({ isSubmitting: false });
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to save HOD score';
      set({ error: message, isSubmitting: false });
      return { success: false, error: message };
    }
  },

  submitHodResponses: async (cycleId) => {
    set({ isSubmitting: true, error: null });
    try {
      const response = await kraCycleService.submitHodResponses(cycleId);
      await get().fetchCycle(cycleId);
      set({ isSubmitting: false });
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to submit HOD review';
      set({ error: message, isSubmitting: false });
      return { success: false, error: message };
    }
  },

  reopenCycle: async (cycleId, reason) => {
    set({ isSubmitting: true, error: null });
    try {
      await kraCycleService.reopenKraCycle(cycleId, reason);
      await get().fetchCycle(cycleId);
      set({ isSubmitting: false });
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to reopen record';
      set({ error: message, isSubmitting: false });
      return { success: false, error: message };
    }
  },

  deleteCycle: async (cycleId, reason) => {
    set({ isSubmitting: true, error: null });
    try {
      const response = await kraCycleService.deleteKraCycle(cycleId, reason);
      set({ isSubmitting: false, selectedCycle: null });
      return { success: true, message: response.data?.message };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to delete record';
      set({ error: message, isSubmitting: false });
      return { success: false, error: message };
    }
  },
}));

export default useKraCycleStore;
